import { Card } from '@/design-system'
import type { Component, JSX } from 'solid-js'
import { Show, onCleanup, onMount } from 'solid-js'
import { AuthBackdrop } from './AuthBackdrop'
import { PeatchLogo } from './PeatchLogo'

interface AuthLayoutProps {
  children: JSX.Element
  title: string
  subtitle?: string | undefined
  promo?: JSX.Element
  promoTone?: 'primary' | 'secondary'
  roleSwitch?: JSX.Element
  mobileBanner?: JSX.Element
  mode?: 'login' | 'signup'
}

const MODE_MIN_H: Record<'login' | 'signup', string> = {
  login: 'lg:min-h-[640px]',
  signup: 'lg:min-h-[700px]',
}

export const AuthLayout: Component<AuthLayoutProps> = (props) => {
  onMount(() => {
    if (!props.promo) {
      return
    }
    const prevTheme = document.documentElement.dataset.theme
    document.documentElement.dataset.theme = 'light'
    onCleanup(() => {
      if (prevTheme === undefined) {
        delete document.documentElement.dataset.theme
      } else {
        document.documentElement.dataset.theme = prevTheme
      }
    })
  })

  return (
    <Show
      when={props.promo}
      fallback={
        <div class="min-h-screen flex items-center justify-center bg-[var(--color-background)] px-4 py-12">
          <div class="w-full max-w-md">
            <Show when={props.roleSwitch}>
              <div class="mb-6">{props.roleSwitch}</div>
            </Show>

            <div class="flex items-center justify-center gap-3 mb-8 animate-[fade-in_400ms_var(--ease-component)]">
              <PeatchLogo size={64} />

              <span class="font-[family-name:var(--font-display)] text-[length:var(--text-2xl)] font-semibold tracking-[-0.02em] text-[var(--color-text)]">
                Peatch
              </span>
            </div>

            <div class="text-center mb-8 animate-[fade-in_400ms_var(--ease-component)_100ms_both]">
              <h1 class="text-balance text-[length:var(--text-2xl)] font-[var(--font-bold)] text-[var(--color-text)] leading-[var(--leading-display)]">
                {props.title}
              </h1>
              <Show when={props.subtitle}>
                <p class="mt-4 text-[length:var(--text-base)] text-[var(--color-text-muted)]">
                  {props.subtitle}
                </p>
              </Show>
            </div>

            <Show when={props.mobileBanner}>
              <div class="mb-6 lg:hidden">{props.mobileBanner}</div>
            </Show>

            <Card
              padded={false}
              class="p-6 sm:p-8 animate-[fade-in_400ms_var(--ease-component)_200ms_both]"
            >
              {props.children}
            </Card>
          </div>
        </div>
      }
    >
      <div class="relative min-h-screen overflow-hidden bg-[var(--color-background)]">
        <AuthBackdrop tone={props.promoTone === 'secondary' ? 'secondary' : 'primary'} />

        <div class="relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-4 lg:px-12 lg:py-5">
          <div class="flex items-center justify-center gap-2" data-ac-brand>
            <PeatchLogo size={40} />
            <span class="font-[family-name:var(--font-display)] text-[19px] font-semibold tracking-[-0.02em] text-[var(--color-text)]">
              Peatch
            </span>
          </div>

          <div
            data-ac-card
            class="ac-glass-card flex w-full max-w-sm flex-col overflow-hidden rounded-[var(--radius-xl)] lg:max-w-4xl lg:flex-row xl:max-w-5xl"
          >
            <div
              class="relative hidden lg:flex lg:w-1/2 lg:flex-col"
              classList={{
                'bg-gradient-to-b from-[var(--color-secondary-50)] to-[var(--color-primary-50)]':
                  props.promoTone === 'secondary',
                'bg-gradient-to-b from-[var(--color-primary-50)] to-[var(--color-secondary-50)]':
                  props.promoTone !== 'secondary',
              }}
              data-ac-promo
              data-testid="auth-promo"
            >
              {props.promo}
            </div>

            <div
              data-testid="auth-card"
              class={`relative flex w-full flex-col gap-6 p-5 sm:p-6 lg:w-1/2 lg:gap-7 lg:p-8 ${
                props.mode ? MODE_MIN_H[props.mode] : ''
              }`}
            >
              <div class="text-center" data-ac-header>
                <h1 class="text-balance font-[family-name:var(--font-display)] text-[length:var(--text-xl)] font-[var(--font-bold)] text-[var(--color-text)] leading-[var(--leading-display)] lg:text-[length:var(--text-2xl)]">
                  {props.title}
                </h1>
                <Show when={props.subtitle}>
                  <p class="mt-3 text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
                    {props.subtitle}
                  </p>
                </Show>
              </div>

              {props.roleSwitch}

              <Show when={props.mobileBanner}>
                <div class="lg:hidden">{props.mobileBanner}</div>
              </Show>

              {props.children}
            </div>
          </div>
        </div>
      </div>
    </Show>
  )
}
